import { createContext, useContext, useState, useMemo, useCallback } from 'react';
import { useDataFetchingClient } from 'data-fetching-client';
import { GET_ASSOCIATED_CAMPAIGN_FIELD_NAME } from './hooks/useFetchAssociatedCampaign';
import { useSetAssociationsMutation } from 'campaigns-lib/__generated__/chirp/com/hubspot/campaigns/association/rpc/CampaignsAssociationRpc.hooks';
import Raven from 'raven-js';
import { SENTRY_KEYS } from 'campaigns-lib/constants/sentry';
import CampaignsPlatformEditModal from './CampaignsPlatformEditModal';
import { AssociatableObjectTypesMap } from '../constants/AssetTypes';
import { saveCampaign } from './api/CampaignsAssociation';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const CampaignsContext = /*#__PURE__*/createContext(undefined);
export default function CampaignsProvider({
  children
}) {
  const [isOneToMany, setIsOneToMany] = useState(false);
  const [initialCampaigns, setInitialCampaigns] = useState([]);
  const [updatedCampaigns, setUpdatedCampaigns] = useState([]);
  const [showEditCampaignModal, setShowEditCampaignModal] = useState(false);
  const [editCampaignAssetData, setEditCampaignAssetData] = useState(null);
  const showEditCampaignActionModal = useCallback(assetData => {
    setEditCampaignAssetData(assetData);
    setShowEditCampaignModal(true);
  }, []);
  const closeEditCampaignActionModal = useCallback(() => {
    setShowEditCampaignModal(false);
    setEditCampaignAssetData(null);
  }, []);
  return /*#__PURE__*/_jsxs(CampaignsContext.Provider, {
    value: {
      initialCampaigns,
      updatedCampaigns,
      setInitialCampaigns,
      setUpdatedCampaigns,
      isOneToMany,
      setIsOneToMany,
      showEditCampaignActionModal
    },
    children: [children, showEditCampaignModal && editCampaignAssetData && /*#__PURE__*/_jsx(CampaignsPlatformEditModal, {
      objectType: editCampaignAssetData.objectType,
      assetId: editCampaignAssetData.assetId,
      crmIdProperty: editCampaignAssetData.crmIdProperty,
      onClose: closeEditCampaignActionModal
    })]
  });
}
export function useCampaignsContext() {
  const context = useContext(CampaignsContext);
  const client = useDataFetchingClient();
  const [setAssociations] = useSetAssociationsMutation();
  return useMemo(() => ({
    setInitialCampaigns: context ? context.setInitialCampaigns : () => {},
    setUpdatedCampaigns: context ? context.setUpdatedCampaigns : () => {},
    setIsOneToMany: context ? context.setIsOneToMany : () => {},
    initialCampaigns: context ? context.initialCampaigns : [],
    updatedCampaigns: context ? context.updatedCampaigns : [],
    isOneToMany: context ? context.isOneToMany : false,
    showEditCampaignActionModal: context ? context.showEditCampaignActionModal : () => {},
    saveCampaign: ({
      objectType,
      assetId,
      crmIdProperty
    }) => {
      if (context && context.updatedCampaigns.length > 0) {
        const campaignGuids = context.updatedCampaigns.map(campaign => campaign.campaignGuid);
        if (objectType === AssociatableObjectTypesMap.MARKETING_EMAIL && context.isOneToMany) {
          return setAssociations({
            variables: {
              assetObjectTypeId: objectType,
              crmAssetIds: [Number(assetId)],
              campaignGuids,
              primaryCampaignGuid: campaignGuids[0]
            }
          }).then(() => {
            client.cache.evict({
              fieldName: GET_ASSOCIATED_CAMPAIGN_FIELD_NAME
            });
            context.setInitialCampaigns(context.updatedCampaigns);
            context.setUpdatedCampaigns([]);
          }).catch(error => Raven.captureException(error, {
            fingerprint: [SENTRY_KEYS.CAMPAIGNS_PLATFORM_UPDATE_FAILED],
            tags: {
              lib: 'campaigns-lib'
            }
          }));
        } else {
          return saveCampaign({
            objectType,
            crmIdProperty,
            selectedAssetCrmId: assetId,
            currentCampaignGuids: context.initialCampaigns.map(campaign => campaign.campaignGuid),
            updatedCampaignGuids: context.updatedCampaigns.map(campaign => campaign.campaignGuid)
          }).then(() => {
            client.cache.evict({
              fieldName: GET_ASSOCIATED_CAMPAIGN_FIELD_NAME
            });
            context.setInitialCampaigns(context.updatedCampaigns);
            context.setUpdatedCampaigns([]);
          }).catch(error => Raven.captureException(error, {
            fingerprint: [SENTRY_KEYS.CAMPAIGNS_PLATFORM_UPDATE_FAILED],
            tags: {
              lib: 'campaigns-lib'
            }
          }));
        }
      }
      return Promise.resolve();
    }
  }), [client, context, setAssociations]);
}